import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useBrand, useTenantPath } from "@/lib/brand";
import { assetUrl } from "@/lib/api";
import { withPreview } from "@/lib/sitePreview";
import { wordmarkParts, addressLine, hoursLine } from "@/lib/agencyDetails";

/**
 * The header, footer and mobile bar the design draws, filled from the
 * agency's own record.
 *
 * Not one word of copy is written here. The wordmark is the agency's name
 * split on its last word, which is what the design shows for a two-word
 * agency and degrades to a single line for a one-word one; the nav is the
 * pages the site already has; the footer's regulatory lines, badges, areas
 * and contact details all come off the brand blob. An agency with none of a
 * thing gets no heading for it rather than an empty column.
 */

function Wordmark({ name, logo }) {
  const [top, tail] = wordmarkParts(name);
  if (logo) {
    // An uploaded mark wins. Boxed so it reserves its width before it loads,
    // for the same reason SiteHeader's is.
    return (
      <span className="wordmark" style={{ display: "inline-flex", width: 180, height: 48, alignItems: "center" }}>
        <img src={assetUrl(logo)} alt={name} decoding="async"
             style={{ maxWidth: "100%", maxHeight: "100%", width: "auto", objectFit: "contain" }} />
      </span>
    );
  }
  return (
    <span className="wordmark">
      <span className="name">{top}</span>
      {/* The space is not decoration. The link's aria-label is
          "<name>, home", and WCAG 2.5.3 wants the visible text inside the
          accessible name; without it the text reads "WindlebrookEstates",
          which "Windlebrook Estates, home" does not contain. A whitespace
          -only anonymous flex item is not rendered, so nothing moves. */}
      {tail && <> <span className="sub">{tail}</span></>}
    </span>
  );
}

export function EstateHeader({ dark = false, preview = true }) {
  const brand = useBrand();
  const biz = brand.business || {};
  const tenantPath = useTenantPath();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const p = (href) => withPreview(href, preview);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const links = [
    { to: tenantPath("properties"), label: "Buy" },
    { to: tenantPath("sell"), label: "Sell" },
    { to: tenantPath("let"), label: "Let" },
    { to: tenantPath("guides"), label: "Guides" },
    { to: tenantPath("about"), label: "About" },
    { to: tenantPath("contact"), label: "Contact" },
  ];

  return (
    <>
      <header className={`site-header${dark ? " dark" : ""}`} data-testid="estate-header">
        <div className="container nav">
          <Link to={p(tenantPath(""))} aria-label={`${biz.name || "Home"}, home`}>
            <Wordmark name={biz.name} logo={dark ? null : brand.assets?.logo_url} />
          </Link>
          <nav aria-label="Primary">
            <ul className="nav-links">
              {links.map((l) => (
                <li key={l.label}>
                  <Link to={p(l.to)}
                        aria-current={location.pathname === l.to ? "page" : undefined}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
          <div className="nav-cta">
            {biz.phone && (
              <a className="nav-tel" href={`tel:${(biz.phone_intl || biz.phone).replace(/\s/g, "")}`}>
                {biz.phone}
              </a>
            )}
            <Link className="btn sm" to={p(tenantPath("valuation"))}>Value my home</Link>
            <button className="burger" aria-label={open ? "Close menu" : "Open menu"}
                    aria-expanded={open} onClick={() => setOpen((o) => !o)}>
              <span />
            </button>
          </div>
        </div>
      </header>
      {open && (
        <div className="mobile-nav" data-testid="estate-mobile-nav">
          <ul>
            {links.map((l) => <li key={l.label}><Link to={p(l.to)}>{l.label}</Link></li>)}
            <li><Link to={p(tenantPath("register"))}>Register as a buyer</Link></li>
          </ul>
        </div>
      )}
    </>
  );
}

export function EstateFooter({ preview = true }) {
  const brand = useBrand();
  const biz = brand.business || {};
  const tenantPath = useTenantPath();
  const p = (href) => withPreview(href, preview);
  const [top, tail] = wordmarkParts(biz.name);
  const areas = brand.areas_served || [];
  const badges = brand.regulatory?.badges || [];
  const footerLines = brand.regulatory?.footer_lines || [];

  const legal = [
    brand.fees ? { label: "Fees", to: tenantPath("fees") } : null,
    brand.legal?.terms ? { label: "Terms", to: tenantPath("terms") } : null,
    brand.legal?.privacy ? { label: "Privacy", to: tenantPath("privacy") } : null,
    brand.legal?.cookies ? { label: "Cookies", to: tenantPath("cookies") } : null,
  ].filter(Boolean);

  return (
    <footer className="site-footer" data-testid="estate-footer">
      <div className="container">
        <div className="foot-grid">
          <div className="foot-brand">
            <div className="big">{top}<br /><span className="serif-i">{tail}</span></div>
            {biz.elevator_pitch && <p>{biz.elevator_pitch}</p>}
            {badges.length > 0 && (
              <div className="badges">
                {badges.map((b) => <span key={b.label}>{b.label}</span>)}
              </div>
            )}
          </div>

          <nav aria-labelledby="fs-foot-explore">
            <div className="foot-h" id="fs-foot-explore">Explore</div>
            <ul>
              <li><Link to={p(tenantPath("properties"))}>Property for sale</Link></li>
              <li><Link to={p(tenantPath("let"))}>Property to let</Link></li>
              <li><Link to={p(tenantPath("valuation"))}>Instant valuation</Link></li>
              <li><Link to={p(tenantPath("register"))}>Register as a buyer</Link></li>
              <li><Link to={p(tenantPath("guides"))}>Guides</Link></li>
              {legal.map((l) => <li key={l.label}><Link to={p(l.to)}>{l.label}</Link></li>)}
            </ul>
          </nav>

          {areas.length > 0 && (
            <nav aria-labelledby="fs-foot-areas">
              <div className="foot-h" id="fs-foot-areas">Areas</div>
              <ul>
                {areas.slice(0, 8).map((a) => (
                  <li key={a}>
                    <Link to={p(tenantPath(`area/${a.toLowerCase().replace(/\s+/g, "-")}`))}>{a}</Link>
                  </li>
                ))}
              </ul>
            </nav>
          )}

          <div>
            <div className="foot-h" id="fs-foot-contact">Talk to us</div>
            <ul aria-labelledby="fs-foot-contact">
              {biz.phone && (
                <li><a href={`tel:${(biz.phone_intl || biz.phone).replace(/\s/g, "")}`}>{biz.phone}</a></li>
              )}
              {biz.email && <li><a href={`mailto:${biz.email}`}>{biz.email}</a></li>}
              {addressLine(brand.address) && <li>{addressLine(brand.address)}</li>}
              {(brand.opening_hours || []).map(hoursLine).filter(Boolean).slice(0, 3).map((h) => (
                <li key={h}>{h}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="foot-bottom">
          <div>
            © {new Date().getFullYear()} {biz.name}
            {brand.company_number ? ` · Registered in England & Wales ${brand.company_number}` : ""}
          </div>
          <div>{footerLines.join(" · ")}</div>
        </div>
      </div>
    </footer>
  );
}

export function EstateStickyCta({ preview = true }) {
  const brand = useBrand();
  const biz = brand.business || {};
  const tenantPath = useTenantPath();
  const p = (href) => withPreview(href, preview);
  return (
    <div className="sticky-cta" data-testid="estate-sticky-cta">
      <Link className="btn sm" to={p(tenantPath("valuation"))}>Value my home</Link>
      {biz.phone && (
        <a className="btn sm ghost" href={`tel:${(biz.phone_intl || biz.phone).replace(/\s/g, "")}`}>Call us</a>
      )}
    </div>
  );
}
